import { Component, ElementRef, Inject, signal, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { LanguageSelectorComponent } from '../language-selector/language-selector.component';
import { LanguageSwitcherComponent } from '../language-switcher/language-switcher.component';
import { UpdatePasswordDto } from '../../models/dto/updatePasswordDTO';
import { AuthService } from '../../services/auth/auth.service';
import { SpinnerService } from '../../services/spinner/spinner.service';
import { TranslateService } from '@ngx-translate/core';
import { ToasterTypeEnum } from '../../enums/toasterType.enum';
import { ToasterService } from '../../services/toaster/toaster.service';
import { BrowserStorage } from '../../models/browserStorage';
import { PasswordUtils } from '../../utils/passwordUtils';
import { SharedModule } from '../shared.module';
@Component({
  selector: 'app-change-password-modal',
  standalone: true,
  imports: [
    SharedModule,
    MatDialogModule,
    MatCheckboxModule,
    MatInputModule,
    LanguageSelectorComponent,
    MatMenuModule,
    ReactiveFormsModule,
    LanguageSwitcherComponent,
    MatButtonModule
  ],
  templateUrl: './change-password-modal.component.html',
  styleUrls: ['./change-password-modal.component.scss']
})

export class ChangePasswordModalComponent {

  updatePasswordDto = new UpdatePasswordDto();
  currentPassword: string;
  errorEmptyMessage = signal('');
  repeatPassword: string = '';
  errorMessages = {
    currentPassword: signal('login.current_password_empty'),
    newPassword: signal('login.password_empty'),
    repeatPassword: signal('login.confirm_new_password_empty'),
  };

  readonly currentPasswordFormControl = new FormControl('', [Validators.required]);
  readonly newPasswordFormControl = new FormControl('', [Validators.required, PasswordUtils.passwordValidationFn]);
  readonly repeatPasswordFormControl = new FormControl('', [Validators.required]);

  showHeight: boolean = false;
  @ViewChild('form', { static: true }) formElement: ElementRef<HTMLFormElement>;
  userError: boolean = false;
  userValidationErrors: boolean = false;

  passwordVisibility = {
    currentPassword: false,
    newPassword: false,
    repeatPassword: false
  };

  PASSWORD_MIN_LENGTH = PasswordUtils.PASSWORD_MIN_LENGTH;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<ChangePasswordModalComponent>,
    private authService: AuthService,
    private spinnerService: SpinnerService,
    private translateService: TranslateService,
    private toasterService: ToasterService,
  ) {
    this.setupErrorHandling();
  }

  private setupErrorHandling() {
    this.setupErrorControl(this.currentPasswordFormControl, "login.current_password_empty", this.errorMessages.currentPassword);
    this.setupErrorControl(this.newPasswordFormControl, "login.password_empty", this.errorMessages.newPassword);
    this.setupErrorControl(this.repeatPasswordFormControl, "login.confirm_new_password_empty", this.errorMessages.repeatPassword);
  }

  private setupErrorControl(control: FormControl, errorMessageKey: string, errorSignal: (value: string) => void) {
    control.valueChanges.subscribe(() => this.handleError(control, errorMessageKey, errorSignal));
    control.statusChanges.subscribe(() => this.handleError(control, errorMessageKey, errorSignal));
  }

  private handleError(control: FormControl, errorMessageKey: string, errorSignal: (value: string) => void) {
    if (control.hasError('required')) {
      errorSignal(errorMessageKey);
    } else {
      errorSignal('');
    }

    if (control.hasError('pattern')) {
      const errorMessage = PasswordUtils.getFirstErrorMessageAvailable(PasswordUtils.getPasswordPatternErrors(control.value));
      this.errorMessages.newPassword.set(errorMessage);
    }
  }

  cancel() {
    this.dialogRef.close();
  }

  async save(eventKey?: boolean) {
    this.spinnerService.show();

    if (this.validatePasswordFields(eventKey)) {
      this.spinnerService.hide();
      return;
    }

    await this.updatePassword();
  }

  private validatePasswordFields(eventKey?: boolean): boolean {
    if (this.updatePasswordDto.newPassword !== this.repeatPassword) {
      this.updateErrorMessage('login.passwords_dont_match', true, false);
      return true;
    }
    if (!this.updatePasswordDto.isValid()) {
      this.updateErrorMessage(this.updatePasswordDto.__errors[0], true, false);
      this.showHeight = !eventKey;
      return true;
    }
    if (this.newPasswordFormControl.invalid) {
      return true;
    }
    return false;
  }

  private async updatePassword() {
    await this.authService.updatePasswordLogged(this.updatePasswordDto)
    .catch(err => {
      console.error(err);
      this.updateErrorMessage('login.current_password_invalid', false, true);
    })
    .then(async (r) => {
        if (r) {
		  await this.authService.afterLoginActions(r?.['accessToken'])
          this.toasterService.show('login.reset_successfully', ToasterTypeEnum.SUCCESS);
          this.dialogRef.close()
        }
    })
    .finally(() => this.spinnerService.hide());
  }

  private updateErrorMessage(errorMessage: string, userError: boolean, userValidationErrors: boolean) {
    this.userError = userError;
    this.userValidationErrors = userValidationErrors;
    this.errorEmptyMessage.set(errorMessage);
  }

  handleEnterKey(event: Event) {
    if ((event as KeyboardEvent).key === 'Enter') {
      event.preventDefault();
      this.blurInputs();
      this.save(true);
    }
  }

  private blurInputs() {
    const inputs = this.formElement.nativeElement.querySelectorAll('input') as NodeListOf<HTMLInputElement>;
    Array.from(inputs).forEach(input => input.blur());
  }

  togglePasswordVisibility(field: keyof typeof this.passwordVisibility) {
    this.passwordVisibility[field] = !this.passwordVisibility[field];
  }
}
